import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { FormField } from '../../components/FormField.jsx';

const blankRow = () => ({ label: '', price: '', unit: '' });

export default function RentalPricing() {
  const [rows, setRows] = useState([blankRow()]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await Admin.settings.rental.get();
      setRows(
        (r.items || []).length
          ? r.items.map((i) => ({
              label: i.label,
              price: (i.priceCents / 100).toString(),
              unit: i.unit || '',
            }))
          : [blankRow()],
      );
      setNote(r.note || '');
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  const upd = (idx, key) => (e) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: e.target.value } : r)));
  };

  async function save(e) {
    e.preventDefault();
    setErr(null);
    const kept = rows.filter((r) => r.label.trim() || r.price.trim());
    for (const r of kept) {
      if (!r.label.trim()) return setErr('Every price row needs a label.');
      const n = Number(r.price);
      if (!Number.isFinite(n) || n < 0) return setErr(`"${r.label}" needs a valid price.`);
    }
    setSaving(true);
    try {
      await Admin.settings.rental.save({
        items: kept.map((r) => ({
          label: r.label.trim(),
          priceCents: Math.round(Number(r.price) * 100),
          unit: r.unit.trim() || undefined,
        })),
        note,
      });
      setToast('Rental pricing saved — live on the site now.');
      setTimeout(() => setToast(null), 3000);
      await refresh();
    } catch (e2) {
      setErr(e2.data?.issues ? e2.data.issues.map((i) => i.message).join(', ') : e2.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-ink-3">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Rental pricing"
        description="Shown on the public Facility Rental pages. The first row also becomes the default quote when approving a booking. Leave empty to publish no prices."
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-canvas px-4 py-2.5 rounded-full text-sm shadow-modal z-50">
          {toast}
        </div>
      )}

      <form onSubmit={save} className="card space-y-5">
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 items-start">
              <input
                className="input flex-1"
                placeholder="Label — e.g. Banquet hall, full day"
                value={r.label}
                onChange={upd(i, 'label')}
              />
              <div className="relative w-28 shrink-0">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-sm">$</span>
                <input
                  className="input pl-7"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={r.price}
                  onChange={upd(i, 'price')}
                />
              </div>
              <input
                className="input w-32 shrink-0"
                placeholder="per hour"
                value={r.unit}
                onChange={upd(i, 'unit')}
              />
              <button
                type="button"
                className="btn-ghost btn-sm text-danger shrink-0"
                onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : [blankRow()]))}
                aria-label="Remove row"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn-outline btn-sm"
            onClick={() => setRows((prev) => [...prev, blankRow()])}
            disabled={rows.length >= 12}
          >
            + Add price
          </button>
        </div>

        <FormField
          label="Note"
          hint="Optional line under the prices — e.g. deposits, what's included, or 'Contact us for custom events'."
        >
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </FormField>

        {err && (
          <div className="px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-line">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
